import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthService } from '../auth/auth.service';
import * as Entities from '@shared/entities';

@Injectable({ providedIn: 'root' })
export class SystemService {
  private readonly authService = inject(AuthService);

  // Private writable signals for internal state
  private readonly _currentUser = signal<Entities.User | null>(null);
  private readonly _menuType = signal<string>('');
  private readonly _searchText = signal<string>('');
  private readonly _currentDealer = signal<Entities.Retailer | null>(null);
  private readonly _shouldRefreshTab = signal<boolean>(false);
  private readonly _currentEntityId = signal<number>(0);
  private readonly _programsUpdated = signal<boolean>(false);

  // Public readonly signals - preferred API for new code
  readonly currentUser = this._currentUser.asReadonly();
  readonly menuType = this._menuType.asReadonly();
  readonly searchText = this._searchText.asReadonly();
  readonly currentDealer = this._currentDealer.asReadonly();
  readonly shouldRefreshTab = this._shouldRefreshTab.asReadonly();
  readonly currentEntityId = this._currentEntityId.asReadonly();
  readonly programsUpdated = this._programsUpdated.asReadonly();

  /**
   * @deprecated Use `currentUser()` signal instead.
   * Subscribe pattern: use `effect(() => { const user = this.systemService.currentUser(); ... })`
   */
  readonly hasUser = toObservable(this._currentUser);

  /**
   * @deprecated Use `menuType()` signal instead.
   * Subscribe pattern: use `effect(() => { const type = this.systemService.menuType(); ... })`
   */
  readonly menuChanged = toObservable(this._menuType);

  /**
   * @deprecated Use `searchText()` signal instead.
   */
  readonly searchTextChanged = toObservable(this._searchText);

  /**
   * @deprecated Use `currentDealer()` signal instead.
   */
  readonly dealerChanged = toObservable(this._currentDealer);

  /**
   * @deprecated Use `shouldRefreshTab()` signal instead.
   */
  readonly refreshTab = toObservable(this._shouldRefreshTab);

  /**
   * @deprecated Use `currentEntityId()` signal instead.
   */
  readonly entityChanged = toObservable(this._currentEntityId);

  /**
   * @deprecated Use `programsUpdated()` signal instead.
   */
  readonly programsChanged = toObservable(this._programsUpdated);

  changeMenuType(menuType: string): void {
    this._menuType.set(menuType);
  }

  search(searchText: string): void {
    this._searchText.set(searchText);
  }

  setCurrentEntityId(entityId: number): void {
    this._currentEntityId.set(entityId);
  }

  dealerTabChanged(dealer: Entities.Retailer): void {
    this._currentDealer.set(dealer);
  }

  refreshActiveTab(): void {
    this._shouldRefreshTab.set(true);
    // Reset after a tick so it can be triggered again
    setTimeout(() => this._shouldRefreshTab.set(false), 0);
  }

  updatePrograms(): void {
    this._programsUpdated.set(true);
    // Reset after a tick so it can be triggered again
    setTimeout(() => this._programsUpdated.set(false), 0);
  }

  setCurrentUser(user: Entities.User | null): void {
    this._currentUser.set(user);
  }
}
